// src/components/auth/ProtectedRoute.js - Route protection with feature gates
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useBusiness } from '../../hooks/useBusiness';

const ProtectedRoute = ({ children, requireFeature = null, requireBusiness = true }) => {
  const { user, loading, hasBusiness } = useAuth();
  const { businessProfile, loading: businessLoading } = useBusiness();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading || (requireBusiness && businessLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireBusiness && (!hasBusiness || (!user.businessId && !businessProfile))) {
    return <Navigate to="/register" state={{ from: location }} replace />;
  }

  // Check feature access if required
  if (requireFeature && businessProfile) {
    const hasFeature = businessProfile.canUseFeature(requireFeature);
    
    if (!hasFeature) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow p-6 text-center">
            <div className="mx-auto h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Upgrade Required
            </h3>
            <p className="text-gray-600 mb-4">
              This feature requires a higher tier subscription. Upgrade your plan to access {requireFeature}.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.href = '/pricing'}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
              >
                View Pricing Plans
              </button>
              <button
                onClick={() => window.history.back()}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
